/**
 * Extrae el nombre de la tabla y la columna de una sentencia SQL "ALTER TABLE <tabla> ADD COLUMN <columna> ..."
 */
export function parseAlterTableAddColumn(sql: string): { table: string; column: string } | null {
  const match = sql.match(/^\s*ALTER\s+TABLE\s+([^\s]+)\s+ADD\s+COLUMN\s+([^\s;]+)/i);
  if (!match) return null;
  return {
    table: match[1],
    column: match[2],
  };
}

/**
 * Filtra asincrónicamente una lista de migraciones SQL dejando solo aquellas sentencias ALTER TABLE
 * cuyas columnas aún NO existan en la tabla destino (según PRAGMA table_info).
 */
export async function filterPendingMigrations(
  migrations: string[],
  getTableColumns: (table: string) => Promise<Set<string>>
): Promise<string[]> {
  const tableColumnsCache = new Map<string, Set<string>>();
  const pending: string[] = [];

  for (const sql of migrations) {
    const alterInfo = parseAlterTableAddColumn(sql);
    if (!alterInfo) {
      // Sentencia que no es ALTER TABLE ADD COLUMN (ej. CREATE INDEX IF NOT EXISTS)
      pending.push(sql);
      continue;
    }

    const tableName = alterInfo.table.toLowerCase();
    const columnName = alterInfo.column.toLowerCase();

    if (!tableColumnsCache.has(tableName)) {
      const cols = await getTableColumns(tableName);
      tableColumnsCache.set(tableName, cols);
    }

    const existingCols = tableColumnsCache.get(tableName)!;
    if (!existingCols.has(columnName)) {
      pending.push(sql);
    }
  }

  return pending;
}

/**
 * Versión síncrona de filterPendingMigrations para drivers síncronos como sql.js local.
 */
export function filterPendingMigrationsSync(
  migrations: string[],
  getTableColumnsSync: (table: string) => Set<string>
): string[] {
  const tableColumnsCache = new Map<string, Set<string>>();
  const pending: string[] = [];

  for (const sql of migrations) {
    const alterInfo = parseAlterTableAddColumn(sql);
    if (!alterInfo) {
      pending.push(sql);
      continue;
    }

    const tableName = alterInfo.table.toLowerCase();
    const columnName = alterInfo.column.toLowerCase();

    if (!tableColumnsCache.has(tableName)) {
      const cols = getTableColumnsSync(tableName);
      tableColumnsCache.set(tableName, cols);
    }

    const existingCols = tableColumnsCache.get(tableName)!;
    if (!existingCols.has(columnName)) {
      pending.push(sql);
    }
  }

  return pending;
}
